import { ErrorHandler, Injectable } from '@angular/core';
import { JL } from 'jsnlog';
import { CustomWindow } from 'app/@types';
import { AppDataService } from 'app/services';
import { Utils } from 'app/utils';
import * as ErrorStackParser from 'error-stack-parser';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MessageService } from 'primeng/api';

declare let window: CustomWindow;

@Injectable()
export class ErrorProvider extends ErrorHandler {
    cachedMaps: { [key: string]: string } = {};
    constructor(private appData: AppDataService, private deviceService: DeviceDetectorService, private messageService: MessageService) {
        const getAppenderOptions = (): JL.JSNLogAjaxAppenderOptions => ({
            bufferSize: 20,
            storeInBufferLevel: 1000,
            level: 4000,
            sendWithBufferLevel: 4000,
            url: window['apiUrl'] + 'v1/api/logging/WebLogging',
            beforeSend: (xhr: XMLHttpRequest) => {
                const token = this.appData.authData.access_token;
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.setRequestHeader('ConversationId', Utils.getConversationId());
            }
        });

        const serverAppender = JL.createAjaxAppender('serverAppender');

        if (serverAppender !== undefined && serverAppender.setOptions) {
            const options = getAppenderOptions();
            serverAppender.setOptions(options);
        } else {
            throw new Error('Appender is undefined.');
        }

        JL().setOptions({
            appenders: [serverAppender]
        });

        super();
    }

    getSourceMap(mapUrl: string) {
        const xmlHttp = new XMLHttpRequest();
        xmlHttp.open('GET', mapUrl, false); // false for synchronous request
        xmlHttp.send(null);
        return xmlHttp.responseText;
    }

    override async handleError(error: any) {
        // Handle error by Angular
        super.handleError(error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error });

        const url = window.location.href;

        let errorObj: any = null;
        if (error.originalError) {
            errorObj = error.originalError;
        }
        else {
            errorObj = error;
        }

        const sourceMapLocal = window['sourceMap'];
        if (sourceMapLocal) {
            const correctedStack = await this.getStackFrame(errorObj, sourceMapLocal);
            error.stack = correctedStack;
            errorObj.stack = correctedStack;
        }

        // Pass error to the window.onerror handler
        if (window.onerror) {
            window.onerror(error.message, url, 0, 0, errorObj);
        }

        JL().fatalException({
            Name: error.name,
            Details: error.message,
            Browser: `${this.deviceService.browser} version ${this.deviceService.browser_version}`,
            ParsedStack: error.stack
        }, error);
    }

    getStringifiedStack(errorObj: any, errorStack: StackFrame[]): string{

        let result = errorStack.map(sf => {
            const functionName = sf.getFunctionName() ? sf.getFunctionName() : '';
            return `at ${functionName}(${sf.fileName}:${sf.lineNumber}:${sf.columnNumber})`;
        }).join('\n ');

        result = `${errorObj.name}: ${errorObj.message} ${result}`;
        return result;
    }

    async getStackFrame(errorObj: any, sourceMap: any) {

        let result = '';
        if (errorObj != null) {
            const errorStack = ErrorStackParser.parse(errorObj);

            for (let stackItem of errorStack) { stackItem = await this.correctStackFrame(stackItem, sourceMap); }

            result = this.getStringifiedStack(errorObj, errorStack);

            // clear cached Maps array
            for (const prop in this.cachedMaps) {
                if (this.cachedMaps.hasOwnProperty(prop)) {
                    delete this.cachedMaps[prop];
                }
            }
        }

        return result;
    }

    async correctStackFrame(stackframeItem: StackFrame, sourceMap: any) {
        let result = stackframeItem;

        let stackUrl = stackframeItem.fileName;
        const stackLine = stackframeItem.lineNumber;
        const stackColumn = stackframeItem.columnNumber;

        if (stackUrl && !stackUrl.endsWith('.js')) {
            const hashIndexOnStackUrl = stackUrl.indexOf('?v=');
            if (hashIndexOnStackUrl > -1) {
                stackUrl = stackUrl.substring(0, hashIndexOnStackUrl);
            }

            const mapStackUrl: string = stackUrl + '.map';
            let rawStackSourceMap;

            if (this.cachedMaps[mapStackUrl]) { rawStackSourceMap = this.cachedMaps[mapStackUrl]; }
            else {
                rawStackSourceMap = this.getSourceMap(mapStackUrl);
                this.cachedMaps[mapStackUrl] = rawStackSourceMap;
            }

            if (rawStackSourceMap !== undefined &&
                rawStackSourceMap.toString().length > 0) {

                try {
                    result = await sourceMap.SourceMapConsumer.with(rawStackSourceMap, null, (consumer: any) => {
                        const stackObjectLocal = consumer.originalPositionFor({
                            line: stackLine,
                            column: stackColumn
                        });

                        stackframeItem.fileName = stackObjectLocal.source;
                        stackframeItem.lineNumber = stackObjectLocal.line;
                        stackframeItem.columnNumber = stackObjectLocal.column;

                        return stackframeItem;
                    });

                }
                catch (err) {
                    // correct stack frame failed
                }

            }
        }

        return result;
    }
}
